import { PortfolioItem } from '../portfolio/PortfolioPage/types'
import { MediaSizes } from '../products/ProductsPage/types'
import { Post } from './types'

export type TaxonomyTerm = {
	id: number
	count: number
	name: string
	slug: string
	taxonomy: string
}

export async function fetchTaxonomyTerms(taxonomyName: string) {
	return fetchWP<TaxonomyTerm[]>(taxonomyName)
}

type MediaResponse = {
	id: number
	title: {
		rendered: string
	}
	mime_type: string
	media_details: {
		filesize: number
	}
	source_url: string
}

export async function fetchMediaResources([
	search,
	productTypeFilterIds,
	projectTypeFilterIds,
]: [string, number[], number[]]) {
	return fetchWP<MediaResponse[]>('media', {
		media_folder: 37,
		product_categories: productTypeFilterIds,
		project_types: projectTypeFilterIds,
		search,
	})
}

export type WordpressParams = {
	[key: string]: string | number | number[] | string[]
}

export function formatWordpressParams(params: WordpressParams) {
	return Object.entries(params)
		.flatMap(([key, value]) => {
			if (Array.isArray(value)) {
				const vals = value.map(v => encodeURIComponent(v.toString())).join(',')

				if (vals) {
					return key + '=' + vals
				}

				return []
			}

			if (value) {
				return key + '=' + encodeURIComponent(value)
			}

			return []
		})
		.join('&')
}

export function formatWordpressUrl(postType: string, params: WordpressParams) {
	const query = formatWordpressParams(params)

	return `/wp-json/wp/v2/${postType}?${query}`
}

export async function fetchWordpressJson<PostType extends Post>(url: string) {
	const res = await fetch(url)

	const data = (await res.json()) as PostType[]

	const wpTotal = parseInt(res.headers.get('x-wp-total')!)
	const wpTotalPages = parseInt(res.headers.get('x-wp-totalpages')!)

	return { data, wpTotal, wpTotalPages }
}

export async function fetchWP<ReturnType>(
	postType: string,
	params: WordpressParams = {},
): Promise<ReturnType> {
	const combinedParams = { per_page: 100, ...params }

	const query = formatWordpressParams(combinedParams)

	const res = await fetch(`/wp-json/wp/v2/${postType}?${query}`)

	return await res.json()
}

export function formatFileSize(size: number) {
	if (size >= 1_000_000_000) {
		return Math.round(size / 1_000_000_000) + ' gb'
	}

	if (size >= 1_000_000) {
		return Math.round(size / 1_000_000) + ' mb'
	}

	if (size >= 1_000) {
		return Math.round(size / 1_000) + ' kb'
	}

	return size + ' b'
}

export function sizesToSrcset(sizeData: MediaSizes): string {
	const sizes = Object.entries(sizeData)

	sizes.sort(([, a], [, b]) => a.width - b.width)

	return sizes.map(([, size]) => `${size.source_url} ${size.width}w`).join(',')
}

export function isString(a: unknown): a is string {
	return typeof a === 'string'
}

declare global {
	interface Document {
		startViewTransition?: (fn: () => void) => void
	}
}

export function updateView(action: () => void) {
	if (document.startViewTransition) {
		document.startViewTransition(action)
	} else {
		action()
	}
}
